// One-time native-app-shell setup (status bar color, splash screen
// dismissal, keyboard resize behavior) for Capacitor builds. No-ops on
// the plain web/PWA build.
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNativePlatform } from './backgroundLocation';

// App's brand background (matches vite.config.js's PWA theme_color and
// capacitor-assets' generated icon/splash background) -- the app is a
// single dark theme on native regardless of the web build's light/dark
// toggle, since the status bar and splash are configured once at launch,
// before ThemeContext has resolved a per-user preference.
const BRAND_BG = '#1A1A1A';

// Call once, as early as possible (before the auth/data loading gate
// resolves) -- status bar and keyboard behavior should be set up
// immediately, independent of when the splash screen gets dismissed.
export async function initNativeShell() {
  if (!isNativePlatform()) return;
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Dark }); // light icons/text, for a dark background
    await StatusBar.setBackgroundColor({ color: BRAND_BG });
  } catch { /* not fatal -- status bar styling is cosmetic */ }
  try {
    // Resizes the WebView's visible viewport when the keyboard opens
    // (rather than resizing the whole native view), so fixed-position
    // headers/footers in the app don't jump around on Android.
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
  } catch { /* not fatal */ }
}

// Call once the app has real content to show (auth state resolved) --
// launchAutoHide is disabled in capacitor.config.ts specifically so this
// controls the dismissal instead of the splash disappearing before the
// app is actually ready to render.
export async function hideSplashScreen() {
  if (!isNativePlatform()) return;
  try {
    await SplashScreen.hide();
  } catch { /* not fatal */ }
}

// Tells Capgo's live-update plugin the current bundle loaded and rendered
// successfully. Must be called within capacitor.config.ts's
// appReadyTimeout (10s default) of a bundle swap, or the plugin assumes
// the new bundle is broken and automatically rolls back to the last
// known-good one -- so this is called from the same place as
// hideSplashScreen() (once auth/data loading has actually resolved),
// not on script load, which would defeat the rollback safety check.
export async function notifyAppReady() {
  if (!isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch { /* not fatal */ }
}
